import React from 'react';
import styled from 'styled-components';
import { AlertsLegendType, LegendSetsType } from '../utilities/interfaces';

interface MapLegendType {
    legendColors: [];
    legendLabels: [];
    alertsLegend: AlertsLegendType[];
    legendSets: LegendSetsType[];
    legendType: string;
    legendOverlays: []; // Overlays text on top of the colors.  Currently only works with block.
    legendUnits: string;
    applyPresetTopPadding?: boolean;
}

export const MapLegend = ({
    legendColors,
    legendLabels,
    alertsLegend,
    legendSets,
    legendType,
    legendOverlays,
    legendUnits,
    applyPresetTopPadding = false
}: MapLegendType) => {
    return (
        <div>
            {
                !legendSets
                && legendColors?.length > 0
                && (legendType === 'block'
                    ? <LegendBlockSet applyPresetTopPadding={applyPresetTopPadding}>
                        {legendColors?.map((color, index) => {
                            const overlayText = legendOverlays && legendOverlays?.length > index ? legendOverlays[index] : '';
                            return <LegendBlock key={`legend-block-set-${color}-${overlayText}-${index}`} color={color}>{overlayText}</LegendBlock>;
                        }
                        )}
                    </LegendBlockSet>
                    : <LegendSvg applyPresetTopPadding={applyPresetTopPadding}>
                        <defs>
                            <linearGradient id='grad' x1='0%' y1='0%' x2='100%' y2='0%'>
                                {legendColors?.map((color, index) => {
                                    return <stop key={`legend-colors-gradient-${color}-${legendColors?.length * index}`} offset={(100 / legendColors?.length * index + '%')} stopColor={color} />;
                                })}
                            </linearGradient>
                        </defs>
                        <rect x='0' y='0' width='100%' height='100%' fill='url(#grad)' />
                    </LegendSvg>)
            }
            {
                !legendSets && legendLabels?.length > 0 && <LegendLabelContainer applyPresetTopPadding={applyPresetTopPadding}>
                    {
                        legendLabels?.map((label, index) => {
                            return <LegendLabelTextContainer key={`legend-label-${label}-${index}`}>
                                <LegendLabel>{label}{legendUnits}</LegendLabel>
                            </LegendLabelTextContainer>;
                        })
                    }
                </LegendLabelContainer>
            }
            {
                legendSets?.length > 0 && <LegendSetContainer applyPresetTopPadding={applyPresetTopPadding}>
                    {legendSets?.map((legendSet, index) => {
                        return <LegendSet key={`legend-set-${legendSet?.precipType || index}`}>
                            {legendSet?.colorStops && <LegendSetColorContainer>
                                { legendSet?.colorStops?.map((colorStop, index) => {
                                    return <LegendSetColor key={`legend-set-color-stops-${colorStop?.color}-${index}`} color={colorStop?.color}></LegendSetColor>;
                                }) }
                            </LegendSetColorContainer>}
                            <LegendSetTextContainer>
                                {legendSet?.precipType}
                            </LegendSetTextContainer>
                        </LegendSet>;
                    })}
                </LegendSetContainer>
            }
            {
                alertsLegend?.length > 0 && <AlertLegendContainer applyPresetTopPadding={applyPresetTopPadding}>
                    {
                        alertsLegend?.map((alert, index) => {
                            return <AlertItem key={`alerts-legend-${alert?.Type}-${index}`}>
                                <svg width='20' height='20'>
                                    <rect x='0' y='0' width='100%' height='100%' fill={alert?.FillColor} stroke={alert?.LineColor} />
                                </svg>
                                <AlertText>{alert?.Type}</AlertText>
                            </AlertItem>;
                        })
                    }
                </AlertLegendContainer>
            }
        </div>
    );
};

interface  StyledProps {
    applyPresetTopPadding: boolean;
}

const LegendSvg = styled.svg<StyledProps>`
    position: absolute;
    top: ${props => props.applyPresetTopPadding ? '24px' : '0'};
    left: 0;
    right: 0;
    height: 20px;
    width: 100%;
`;

const LegendLabelContainer = styled.div<StyledProps>`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding-left: 5px;
    padding-right: 5px;
    padding-top: 3px;
    position: absolute;
    top: ${props => props.applyPresetTopPadding ? '44px' : '20px'};
    left: 0;
    right: 0;
    height: 22px;
    background-color: rgba(0, 0,0,0.5);
    font-size: 13px;
`;

const LegendLabelTextContainer = styled.div`
    flex: 1;
    text-align: center;
`;

const LegendLabel = styled.span`
    color: white;
`;

const AlertLegendContainer = styled.div<StyledProps>`
    background-color: rgba(0,0,0,0.5);
    position: absolute;
    display: flex;
    width: 100%;
    justify-content: space-around;
    left: 0;
    right: 0;
    top: ${props => props.applyPresetTopPadding ? '24px' : '0'};
    font-size: 13px;
    color: white;
`;

const AlertItem = styled.div`
    display: flex;
    align-items: center;
    padding-top: 3px;
    padding-bottom: 3px;
`;

const AlertText = styled.span`
    margin-left: 3px;
`;

const LegendSetContainer = styled.div<StyledProps>`
    position: absolute;
    top: ${props => props.applyPresetTopPadding ? '24px' : '0'};
    left: 0;
    right: 0;
    display: flex;
    flex-direction: row;
    font-size: 13px;
    color: #FFFFFF;
`;

const LegendSet = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
`;

const LegendSetColorContainer = styled.div`
    display: flex;
    flex-direction: row;
    height: 20px;
`;

const LegendSetTextContainer = styled.div`
    display: flex;
    flex: 1;
    justify-content: center;
    align-items: center;
    background-color: rgba(0,0,0,0.5);
    height: 20px;
`;

const LegendSetColor = styled.div`
    background-color: ${({ color })=>color};
    flex: 1;
`;

const LegendBlock = styled.div`
    flex: 1;
    font-size: 13px;
    color: #000000;
    text-align: center;
    line-height: 20px;
    background-color: ${({ color }) => color};
`;

const LegendBlockSet = styled.div<StyledProps>`
    display: flex;
    flex-direction: row;
    flex: 1;
    height: 20px;
    top: ${props => props.applyPresetTopPadding ? '24px' : '0'};
    left: 0;
    right: 0;
    position: absolute;
`;
